import { Controller } from '@hotwired/stimulus'

const SHORT_CODE = 'Enter the 6-digit code from the text message.'

export default class extends Controller {
  static targets = [
    'input',
    'boxes',
    'box',
    'submit',
    'submitLabel',
    'resend',
    'resendLabel',
    'error',
  ]

  static values = { wait: { type: Number, default: 60 } }

  connect() {
    this.submitting = false
    this.ready = false
    // A value on connect is a code the server just rejected. A full field
    // ignores new keystrokes, so the next click or keystroke clears it.
    this.staleCode = this.digits().length > 0
    this.paint()
    if (this.hasResendTarget) this.start(this.waitValue)
    this.onFocus = () => this.clearRejectedCode()
    this.onBeforeInput = (event) => {
      if (!this.ready || !this.staleCode || this.submitting) return
      if (typeof event.data !== 'string') {
        this.clearRejectedCode()
        return
      }
      event.preventDefault()
      this.staleCode = false
      this.inputTarget.readOnly = false
      this.inputTarget.disabled = false
      this.inputTarget.value = event.data.replace(/\D/g, '').slice(0, 6)
      this.paint()
      this.clearError()
      if (this.digits().length === 6)
        this.element.requestSubmit(this.submitTarget)
    }
    this.onPointerDown = (event) => {
      const field = this.inputTarget.closest('.mfa-code-field')
      if (!field?.contains(event.target)) return
      this.clearRejectedCode({ focus: true })
    }
    this.inputTarget.addEventListener('focus', this.onFocus)
    this.inputTarget.addEventListener('beforeinput', this.onBeforeInput)
    this.element.addEventListener('pointerdown', this.onPointerDown)
    this.inputTarget.focus()
    this.ready = true
  }

  disconnect() {
    this.stop()
    this.inputTarget.removeEventListener('focus', this.onFocus)
    this.inputTarget.removeEventListener('beforeinput', this.onBeforeInput)
    this.element.removeEventListener('pointerdown', this.onPointerDown)
  }

  onInput() {
    const digits = this.digits()
    if (this.inputTarget.value !== digits) this.inputTarget.value = digits
    this.paint()
    this.clearError()
    if (digits.length === 6 && !this.submitting)
      this.element.requestSubmit(this.submitTarget)
  }

  // Enter would submit the first button. On disable-SMS that button re-sends.
  onKeydown(event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (this.submitting) return
    if (this.digits().length < 6) {
      this.showError()
      this.inputTarget.focus()
      return
    }
    this.element.requestSubmit(this.submitTarget)
  }

  submit(event) {
    if (this.hasResendTarget && event.submitter === this.resendTarget) {
      window.setTimeout(() => this.start(this.waitValue), 0)
      return
    }

    if (event.submitter && event.submitter !== this.submitTarget) return

    if (this.digits().length < 6) {
      event.preventDefault()
      this.showError()
      this.inputTarget.focus()
      return
    }

    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.submitting = true
    this.inputTarget.readOnly = true
    this.submitTarget.classList.add('is-busy')
    if (this.hasSubmitLabelTarget)
      this.submitLabelTarget.textContent = 'Verifying…'
    window.setTimeout(() => {
      this.inputTarget.disabled = true
      this.submitTarget.disabled = true
    }, 0)
  }

  clearRejectedCode({ focus = false } = {}) {
    if (!this.ready || !this.staleCode || this.submitting) return
    this.staleCode = false
    this.inputTarget.readOnly = false
    this.inputTarget.disabled = false
    this.inputTarget.value = ''
    this.paint()
    this.clearError()
    if (focus) this.inputTarget.focus()
  }

  digits() {
    return this.inputTarget.value.replace(/\D/g, '').slice(0, 6)
  }

  paint() {
    const digits = this.digits()
    this.boxTargets.forEach((box, index) => {
      box.textContent = digits[index] || ''
      box.classList.toggle('mfa-code-box--filled', index < digits.length)
      box.classList.toggle(
        'mfa-code-box--next',
        index === digits.length && digits.length < 6,
      )
    })
  }

  showError() {
    this.errorTarget.textContent = SHORT_CODE
    this.errorTarget.classList.add('is-shown')
    this.inputTarget.setAttribute('aria-invalid', 'true')
    this.boxesTarget.classList.add('is-error')
  }

  clearError() {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.remove('is-shown')
    this.inputTarget.removeAttribute('aria-invalid')
    this.boxesTarget.classList.remove('is-error')
  }

  start(seconds) {
    this.stop()
    this.remaining = seconds
    this.renderWait()
    this.timer = window.setInterval(() => {
      this.remaining -= 1
      if (this.remaining <= 0) {
        this.stop()
        this.resendTarget.disabled = false
        this.resendLabelTarget.textContent = 'Send a new code'
        return
      }
      this.renderWait()
    }, 1000)
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer)
    this.timer = null
  }

  renderWait() {
    const minutes = Math.floor(this.remaining / 60)
    const seconds = this.remaining % 60
    this.resendTarget.disabled = true
    this.resendLabelTarget.textContent = `Send a new code in ${minutes}:${String(seconds).padStart(2, '0')}`
  }
}
